import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MLMap, type LngLatBoundsLike } from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import type { Cam } from '../../lib/urlState';
import { KCCR_CENTER } from '../../lib/geo';

export const DEFAULT_CAM: Cam = { lon: KCCR_CENTER[0], lat: KCCR_CENTER[1], zoom: 12.6, pitch: 55, bearing: 20 };
const STYLE = 'https://tiles.openfreemap.org/styles/dark';
const TERRAIN_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

export interface Focus { bounds: LngLatBoundsLike; pitch?: number; key: string }

/**
 * MapLibre basemap with 3D terrain and a deck.gl overlay. `layers` are re-applied whenever they change;
 * `focus` (keyed) fits bounds; `onCamChange` fires after each move for deep-link state.
 */
export default function BaseMap({ layers, initialCam = DEFAULT_CAM, focus, onCamChange, onBearing, terrain = true, className }:
  { layers: Layer[]; initialCam?: Cam; focus?: Focus | null; onCamChange?: (c: Cam) => void; onBearing?: (b: number) => void; terrain?: boolean; className?: string }) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const [ready, setReady] = useState(false);
  const camCb = useRef(onCamChange); camCb.current = onCamChange;
  const bearingCb = useRef(onBearing); bearingCb.current = onBearing;

  useEffect(() => {
    const map = new maplibregl.Map({ container: el.current!, style: STYLE, center: [initialCam.lon, initialCam.lat], zoom: initialCam.zoom, pitch: initialCam.pitch, bearing: initialCam.bearing, maxPitch: 80, attributionControl: { compact: true }, antialias: true });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.on('load', () => {
      map.addSource('terrain', { type: 'raster-dem', tiles: [TERRAIN_TILES], encoding: 'terrarium', tileSize: 256, maxzoom: 15, attribution: 'Terrain: Mapzen/AWS' });
      if (terrain) map.setTerrain({ source: 'terrain', exaggeration: 1 });
      const overlay = new MapboxOverlay({ interleaved: true, layers: [] });
      map.addControl(overlay);
      overlayRef.current = overlay;
      setReady(true);
    });
    map.on('rotate', () => bearingCb.current?.(map.getBearing()));
    map.on('moveend', () => { const c = map.getCenter(); camCb.current?.({ lon: c.lng, lat: c.lat, zoom: map.getZoom(), pitch: map.getPitch(), bearing: map.getBearing() }); });
    mapRef.current = map;
    if (import.meta.env.DEV) (window as unknown as { __map?: MLMap }).__map = map;
    return () => { map.remove(); mapRef.current = null; overlayRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (ready) overlayRef.current?.setProps({ layers }); }, [layers, ready]);
  useEffect(() => { if (ready && focus) mapRef.current?.fitBounds(focus.bounds, { padding: 60, pitch: focus.pitch ?? 55, duration: 900, maxZoom: 15 }); }, [focus?.key, ready]);   // eslint-disable-line react-hooks/exhaustive-deps

  // absolutely positioned so the map never participates in its parent's height calculation (MapLibre resizes its canvas to the container, which would otherwise feed back)
  return <div ref={el} className={className ?? 'absolute inset-0'} />;
}
